// Create an organisation from the admin console.
//
// The id is set once and never changed: it is embedded in object-storage keys
// (tenants/<id>/…) and carried in the OIDC `tenant` claim, so renaming it later
// would orphan data. The display name stays editable.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { adminApi } from '../api/adminClient';
import { Field } from '../components/Field';
import { Button } from '../components/ui';
import { useToast } from '../components/Toast';
import { IconClose } from '../components/Icons';

/** Mirrors the server-side rule so the operator is told before submitting. */
const ID_RULE = /^[a-z0-9-]+$/;

export function CreateTenantDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id.trim() || !name.trim()) {
      setError(t('common.required'));
      return;
    }
    if (!ID_RULE.test(id.trim())) {
      setError(t('admin.customers.idRule'));
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await adminApi.adminCreateTenant(id.trim(), name.trim());
      toast.success(t('admin.customers.created', { name: name.trim() }));
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="dialog" onSubmit={submit} role="dialog" aria-modal="true" aria-labelledby="ct-title">
        <div className="row between">
          <h2 className="dialog__title" id="ct-title">
            {t('admin.customers.createTitle')}
          </h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
            <IconClose width={16} height={16} />
          </button>
        </div>
        <div style={{ marginTop: 16 }}>
          <Field label={t('admin.customers.idLabel')} required hint={t('admin.customers.idHint')}>
            {(p) => (
              <input className="input" {...p} value={id} autoFocus onChange={(e) => setId(e.target.value.toLowerCase())} />
            )}
          </Field>
          <Field label={t('common.name')} required error={error}>
            {(p) => <input className="input" {...p} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
        </div>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={busy}>
            {t('common.create')}
          </Button>
        </div>
      </form>
    </div>
  );
}
